/** 밝기 모드와 색상 팔레트를 브라우저에 독립적으로 저장한다. */
export const THEMES = [
  { id: 'paper', label: '페이퍼 · 선명한 파랑', light: '#0065f6', dark: '#629bff' },
  { id: 'sand', label: '샌드 · 따뜻한 모래', light: '#325ef5', dark: '#9baeff' },
  { id: 'ink', label: '잉크 · 깊은 파랑', light: '#215cc5', dark: '#629bff' },
  { id: 'black-lime', label: '라임 · 싱그러운 연두', light: '#46751b', dark: '#88cf3b' },
  { id: 'charcoal', label: '차콜 · 따뜻한 주황', light: '#a04408', dark: '#ff9546' },
  { id: 'coral', label: '코랄 · 부드러운 산호', light: '#b83c30', dark: '#ff8778' },
  { id: 'slate', label: '슬레이트 · 은은한 보라', light: '#7045b0', dark: '#cab3ff' },
  { id: 'forest', label: '포레스트 · 차분한 초록', light: '#167044', dark: '#75d6a3' },
  { id: 'ocean', label: '오션 · 맑은 청록', light: '#076b89', dark: '#68cde9' },
  { id: 'rose', label: '로즈 · 화사한 장미', light: '#b02e62', dark: '#f79abe' },
] as const;
export type ThemeId = (typeof THEMES)[number]['id'];
export type ThemeMode = 'light' | 'dark' | 'system';
export const DEFAULT_THEME: ThemeId = 'paper';
const IDS: readonly string[] = THEMES.map(t => t.id);
const LEGACY_DARK = ['ink', 'black-lime', 'charcoal', 'coral', 'slate'];
let sessionTheme: ThemeId | undefined;
let sessionMode: ThemeMode | undefined;
function stored(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
export function getTheme(): ThemeId {
  const value = sessionTheme ?? stored('office.theme');
  return IDS.includes(value ?? '') ? value as ThemeId : DEFAULT_THEME;
}
export function getMode(): ThemeMode {
  const value = sessionMode ?? stored('office.mode');
  if (value === 'light' || value === 'dark' || value === 'system') return value;
  // 기존 사용자는 선택한 테마의 밝기를 유지한다.
  const old = stored('office.theme');
  if (old && IDS.includes(old)) return LEGACY_DARK.includes(old) ? 'dark' : 'light';
  return 'system';
}
export function resolvedMode(mode = getMode()): 'light' | 'dark' {
  return mode === 'system'
    ? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : mode;
}
function save(key: string, value: string) {
  try { window.localStorage.setItem(key, value); } catch { /* 세션 내 선택은 유지한다. */ }
}
export function setTheme(id: ThemeId): void {
  // 이전 테마의 밝기를 먼저 보존해야 팔레트 선택이 모드를 바꾸지 않는다.
  sessionMode = getMode();
  save('office.mode', sessionMode);
  sessionTheme = id;
  save('office.theme', id);
  apply();
}
export function setMode(mode: ThemeMode): void {
  sessionMode = mode;
  save('office.mode', mode);
  apply();
}
export function apply(id: ThemeId = getTheme()): void {
  document.documentElement.setAttribute('data-theme', id);
  document.documentElement.setAttribute('data-mode', resolvedMode());
  window.dispatchEvent(new Event('office-theme-change'));
}
/** 시스템 밝기 변경과 다른 탭의 설정 변경을 즉시 반영한다. */
export function watchTheme(): () => void {
  const media = window.matchMedia?.('(prefers-color-scheme: dark)');
  const refresh = () => apply();
  const storage = (event: StorageEvent) => {
    if (event.key === null || event.key === 'office.theme' || event.key === 'office.mode') {
      sessionTheme = undefined;
      sessionMode = undefined;
      apply();
    }
  };
  media?.addEventListener('change', refresh);
  window.addEventListener('storage', storage);
  return () => {
    media?.removeEventListener('change', refresh);
    window.removeEventListener('storage', storage);
  };
}
